import { externalConnections, LOGOUT_REDIRECT_URI_KEY, routes } from '@/constants/Constants';
import { logger } from '@/shared/libs/logger';
import { buildCurrentPageUrl } from '@/shared/utils/url.util';
import { hasLogoutStarted } from './logout';

/**
 * Signing in silently reuses the Auth0 session, so a BFF that keeps answering 401 to a valid TG session would send
 * the user around sign-in forever. A second redirect this soon after the last one is refused instead.
 */
const SIGN_IN_LOOP_WINDOW_MS = 120_000;
const LAST_SIGN_IN_REDIRECT_KEY = 'tg_session_sign_in_redirect_at';

let redirectStarted = false;

/**
 * Sends a browser whose TG session the BFFs rejected to sign in, and back to the current page afterwards.
 * There's no token in the browser to refresh or revoke: signing in replaces the session.
 * Returns false when it refuses to redirect, so the caller shows the error instead.
 */
export function redirectToSignIn(context?: Record<string, unknown>): boolean {
  if (isLeaving()) {
    return true;
  }
  if (signedInMoments(Date.now())) {
    logger.error('TG session rejected right after signing in, not redirecting again', context);
    return false;
  }

  rememberSignInRedirect(Date.now());
  leave(buildSignInUrl(buildCurrentPageUrl()), 'TG session rejected, signing in again', context);
  return true;
}

/**
 * A TG session the BFFs refuse for Landlord Portal, such as a renter's. Signing in again would get the same
 * session, so the user sees the access message instead of looping through login.
 */
export function redirectToAccessLimitation(context?: Record<string, unknown>): void {
  if (!isLeaving()) {
    leave(routes.limitation, 'TG session not allowed for Landlord Portal', context);
  }
}

export function buildSignInUrl(redirectUri: string | null): string {
  const signInUrlString = externalConnections.signin;

  if (redirectUri !== null && URL.canParse(redirectUri) && URL.canParse(signInUrlString)) {
    const signInUrl = new URL(signInUrlString);
    signInUrl.searchParams.set(LOGOUT_REDIRECT_URI_KEY, redirectUri);
    return signInUrl.toString();
  }

  return signInUrlString;
}

// Parallel failing requests can all land here, and a logout may already be leaving: only the first navigates.
function isLeaving(): boolean {
  return redirectStarted || hasLogoutStarted();
}

function leave(target: string, message: string, context?: Record<string, unknown>): void {
  redirectStarted = true;
  logger.info(message, context);
  globalThis.location.replace(target);
}

function signedInMoments(now: number): boolean {
  try {
    const last = Number(globalThis.sessionStorage.getItem(LAST_SIGN_IN_REDIRECT_KEY));
    return last > 0 && now - last < SIGN_IN_LOOP_WINDOW_MS;
  } catch {
    return false;
  }
}

function rememberSignInRedirect(now: number): void {
  try {
    globalThis.sessionStorage.setItem(LAST_SIGN_IN_REDIRECT_KEY, String(now));
  } catch {
    // Without storage the loop guard is off; the redirect still happens.
  }
}
